import { EmptyLabelDefault } from '$provider/constants/global';
import { sentry } from '$shared/helpers/logger/sentry';
import type { MatchRelevance } from '$provider/lib/apis/models/MatchRelevance';
import { data } from '$provider/lib/datas';
import { CvoFacilityFeatures } from './CvoFacilityFeatures';
import { NpdbConfiguration } from './NpdbConfiguration';
import type { ISettings } from '$shared/interfaces/api/ISettings';
import type { IMonitoringConfig } from '$shared/interfaces/api/IMonitoringConfig';
import { schemaData } from '$shared/lib/data/SchemaData';
import { VerificationData } from '$shared/lib/appData/VerificationData';
import type { IAuthenticationSettings } from '$shared/interfaces/api/IAuthenticationSettings';

export default class OrgSettings implements ISettings {
  public readonly AlertRecipients: Array<string>;
  public readonly CredentialingStatuses: Array<string>;
  public readonly DatasetMonitoringSettings: Record<string, IMonitoringConfig>;
  public readonly EnableDatasets: boolean;
  public readonly BetaAccess: boolean;
  public readonly EnableDatasetsWhitelist: Array<string>;
  public readonly LicenseMonitoringSettings: IMonitoringConfig;
  public readonly organizationId: string;
  public readonly orgName: string;
  public readonly ProviderProfileImportSourceWhiteList: string[];
  public readonly NpdbConfiguration: Record<string, any>;
  public readonly EnableCredentialingRequestApi: boolean;
  public readonly DisableDeleteProvider: boolean;
  public readonly MatchRelevancePreference: MatchRelevance;
  public readonly EnableDeactivatingProvider: boolean;
  public readonly EnableProfileMerges: boolean;
  public readonly EnableMonitoringDashboard: boolean;
  public readonly PreventMultipleHomeWorkAddressesAndEmails: boolean;
  public readonly CvoFacilityFeatures: CvoFacilityFeatures | undefined;
  public readonly CvoFeatures: { enrolledInCvoServices?: boolean } | undefined;
  public readonly EnableDatasetsWhitelistFacility: string[];
  public readonly AuthenticationSettings: IAuthenticationSettings | undefined;
  public NpdbConfigurationSegments: Record<string, NpdbConfiguration>;
  public readonly CaqhPoCredentialsSegments: Record<string, any>;

  constructor(organizationId: string, settings: ISettings) {
    this.organizationId = organizationId;
    this.AlertRecipients = settings.AlertRecipients || [];
    this.CredentialingStatuses = settings.CredentialingStatuses;
    this.DatasetMonitoringSettings = this.formatDatasetMonitoring(
      settings.DatasetMonitoringSettings
    );
    this.EnableDatasets = settings.EnableDatasets || false;
    this.EnableDatasetsWhitelist =
      (settings.EnableDatasetsWhitelist && [...settings.EnableDatasetsWhitelist]) || [];
    this.LicenseMonitoringSettings = this.formatLicenseMonitoring(
      settings.LicenseMonitoringSettings
    );
    this.NpdbConfiguration = settings.NpdbConfiguration || null;
    this.orgName = settings.OrganizationName || organizationId;
    this.ProviderProfileImportSourceWhiteList =
      settings?.ProviderProfileImportSourceWhiteList || [];
    this.EnableCredentialingRequestApi = !!settings?.EnableCredentialingRequestApi;
    this.DisableDeleteProvider = !!settings?.DisableDeleteProvider;
    this.MatchRelevancePreference = settings?.MatchRelevancePreference || null;
    this.BetaAccess = settings?.BetaAccess || false;
    this.EnableDeactivatingProvider = settings?.EnableDeactivatingProvider;
    this.EnableProfileMerges = !!settings?.EnableProfileMerges;
    this.EnableMonitoringDashboard = !!settings?.EnableMonitoringDashboard;
    this.PreventMultipleHomeWorkAddressesAndEmails =
      !!settings?.PreventMultipleHomeWorkAddressesAndEmails;
    this.CvoFacilityFeatures =
      (settings?.CvoFacilityFeatures && new CvoFacilityFeatures(settings?.CvoFacilityFeatures)) ||
      undefined;
    this.CvoFeatures = settings?.CvoFeatures;
    this.EnableDatasetsWhitelistFacility = settings?.EnableDatasetsWhitelistFacility || [];
    this.AuthenticationSettings = settings?.AuthenticationSettings;
    this.NpdbConfigurationSegments = NpdbConfiguration.from(
      settings?.NpdbConfigurationSegments || {}
    );
    this.CaqhPoCredentialsSegments = settings?.CaqhPoCredentialsSegments || {};
  }

  get organizationName(): string {
    if (!this.orgName) {
      sentry.warn('OrganizationId/Name is not configured', this);
    }
    return this.orgName || EmptyLabelDefault;
  }

  public getAllEnabledDatasets(): string[] {
    if (!this.EnableDatasets) return [];

    const providerDatasets = this.EnableDatasetsWhitelist;
    const facilityDatasets = this.EnableDatasetsWhitelistFacility;

    const datasets = new Set<string>(providerDatasets.concat(facilityDatasets));
    if (datasets.size === 0) return Object.values(schemaData.Dataset);

    return Array.from(datasets).filter(
      (dataset) => !!VerificationData.data?.getDatasetMetadata(dataset)
    );
  }

  private formatDatasetMonitoring(
    settings?: Record<string, IMonitoringConfig>
  ): Record<string, IMonitoringConfig> | any {
    if (!settings) {
      return {};
    }
    const result = {};
    Object.keys(settings).forEach((dataSet) => {
      const config = settings[dataSet];
      result[dataSet] = {
        enableMonitoring: config.enableMonitoring || false,
        monitoringInterval: config.monitoringInterval || data.MonitoringInterval.Monthly
      };
    });
    return result;
  }

  private formatLicenseMonitoring(settings?: IMonitoringConfig): IMonitoringConfig {
    if (!settings) {
      return {
        enableMonitoring: false,
        monitoringInterval: data.MonitoringInterval.Monthly
      };
    }
    return {
      enableMonitoring: settings.enableMonitoring || false,
      monitoringInterval: settings.monitoringInterval || data.MonitoringInterval.Monthly
    };
  }
}
